import { Person } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useWorkingHoursSummaryReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';

import { useWorkingHoursSummaryReport } from './useWorkingHoursSummaryReport';

export const WorkingHoursSummaryReport = () => {
  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery('(max-height:650px)');

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const apiRef = useGridApiRef();

  const defaultReportSelection: AnalyticsReportProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().startOf('month').toDate(),
      endDate: moment().toDate(),
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
      },
      fallbackToEmployeeReportType: true,
    },
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useWorkingHoursSummaryReportData<object>(
    reportDatasetProps.reportSelection,
  );

  const { table } = useWorkingHoursSummaryReport({ data });

  return (
    <ReportViewLayout
      heading={t('laWorkingHoursSummaryReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  return (
                    <>{`${moment(params?.startDate).format('L')} - ${moment(
                      params?.endDate,
                    ).format('L')}`}</>
                  );
                }}
                renderParamsFields={({ params, onChange }) => {
                  return (
                    <ReportDateRangeSelector
                      startDate={params.startDate}
                      endDate={params.endDate}
                      onRangeChange={(startDate, endDate) =>
                        onChange({
                          ...params,
                          startDate: startDate ?? params.startDate,
                          endDate: endDate ?? params.endDate,
                        })
                      }
                      mode={ReportDateRangeSelectorMode.DateRange}
                    />
                  );
                }}
              />
              <ReportDatasetRowCount
                count={table?.rows?.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table?.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laWorkingHoursSummaryReport')}
                startDate={reportDatasetProps.reportSelection.params?.startDate}
                endDate={reportDatasetProps.reportSelection.params?.endDate}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
      slotProps={{
        content: {
          sx: {
            minHeight: isSmallScreen ? 'calc(100vh - 100px)' : 0,
          },
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        getRowId={(row) => row.uniqueId}
        pinnedColumns={{
          left: ['empId', 'empFirstName', 'empLastName'],
        }}
        columnHeaderHeight={36}
      />
    </ReportViewLayout>
  );
};
